<template>
  <v-row class="template-placeholder-row">
    <v-col :cols="disabled ? 12 : 9" class="template-placeholder-dropdown-col">
      <v-combobox
        ref="combobox"
        :disabled="disabled"
        style="backdrop-filter: blur(1px)"
        outlined
        v-model="optionDisplay"
        :dense="dense"
        :items="options"
        :label="label || $t(`components.template.editor.placeholder.token`)"
        @change="onChangeSelection"
        autofocus
        :persistent-hint="Boolean(persistentHint)"
        :hint="hint"
      />
    </v-col>
    <v-col v-if="!disabled" cols="3" class="template-placeholder-format-col">
      <v-checkbox v-model="isFormatter" :style="dense ? 'margin-top: 2px' : ''" @change="onChangeSelection">
        <template #label>
          <small> {{ $t(`components.template.editor.placeholder.format`) }} </small>
        </template>
      </v-checkbox>
    </v-col>
    <v-col cols="12" :class="persistentHint ? 'mt-n4' : 'mt-n8'" v-if="isFormatter">
      <v-select
        style="backdrop-filter: blur(1px)"
        outlined
        v-model="formatter"
        :dense="dense"
        :items="formatters"
        @change="onChangeSelection"
        :label="$t(`components.template.editor.placeholder.formatOptions`)"
      >
        <template #item="{ item }"> {{ $t(`components.template.editor.placeholder.formatters.${item}`) }} </template>
        <template #selection="{ item }">
          {{ $t(`components.template.editor.placeholder.formatters.${item}`) }}
        </template>
      </v-select>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { TemplateTokenConfig } from "@/lib/configuration/template-token.configuration";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { TokenCategoriesEnum } from "@/lib/enum/templateEnums/tokenCategories.enum";
import { TemplateFormatterEnum } from "@/lib/enum/TemplateFormatters.enum";
import { ITemplateUrlContext } from "@/lib/interfaces/template/templateContext.interface";
import { IAppraisal } from "@/models/appraisal.entity";
import { IClearanceApproval } from "@/models/clearance-approval.entity";
import { IClearance } from "@/models/clearance.entity";
import { IInvoiceParty } from "@/models/invoice-party.entity";
import { PartnerEntity } from "@/models/partnerEntity";
import { IRental } from "@/models/rental.entity";
import { IRepairEstimate } from "@/models/repair-estimate.entity";
import { IReport } from "@/models/report.entity";
import { ISignRequest } from "@/models/sign-request.entity";
import { IThg } from "@/models/thg.entity";
import { Contact } from "@/models/user-contact.entity";
import { IUser } from "@/models/User/IUser";
import { MrfiktivFormDocumentGen, MrfiktivUserViewmodelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { SignDigitalSignatureViewModelGen } from "@/services/sign/v1/data-contracts";
import { ThgBillingViewmodelGen } from "@/services/thg/v1/data-contracts";
import { ConfigModule } from "@/store/modules/config";
import { UserModule } from "@/store/modules/me-user.store";
import { Component, Prop, Vue } from "vue-property-decorator";
import { displayLabelForToken } from "@/components/sign-document/v2/document-token-field-types";

export const TokenUrls: ITemplateUrlContext = {
  request: `${TokenCategoriesEnum.URL}.request`,
  report: `${TokenCategoriesEnum.URL}.report`,
  sharing: `${TokenCategoriesEnum.URL}.sharing`
};

@Component({ components: {} })
export default class TemplateEditorPlaceholderDropdown extends Vue {
  @Prop()
  partnerId?: string;

  @Prop({ default: false })
  dense?: boolean;

  @Prop({ default: false })
  disabled?: boolean;

  @Prop({ default: "" })
  initialOption?: string;

  /**
   * Optional selection of which placeholder types are available for selection
   */
  @Prop() context?: {
    report?: boolean;
    partner?: boolean;
    appraisal?: boolean;
    thg?: boolean;
    user?: boolean;
    billing?: boolean;
    form?: boolean;
    request?: boolean;
    url?: boolean;
    sign?: boolean;
    my?: boolean;
    rental?: boolean;
    repairEstimate?: boolean;
    clearanceApproval?: boolean;
    clearance?: boolean;
  };

  @Prop({ default: false })
  persistentHint?: boolean;

  @Prop({ default: "" })
  label?: string;

  @Prop({ default: "" })
  hint?: string;

  formatter = "";

  isFormatter = false;

  option = this.initialOption ?? "";
  hasEditedOption = false;

  private readonly url = TokenUrls;

  private my: Partial<MrfiktivUserViewmodelGen> = {
    firstName: "my.firstName",
    lastName: "my.lastName",
    userName: "my.userName",
    address: {
      street: "my.address.street",
      zip: "my.address.zip",
      city: "my.address.city",
      state: "my.address.state",
      countryCode: "my.address.countryCode"
    },
    contact: { email: "my.contact.email", phone: "my.contact.phone" }
  };

  private user: Partial<MrfiktivUserViewmodelGen> = {
    firstName: "user.firstName",
    lastName: "user.lastName",
    userName: "user.userName",
    address: {
      street: "user.address.street",
      zip: "user.address.zip",
      city: "user.address.city",
      state: "user.address.state",
      countryCode: "user.address.countryCode"
    },
    contact: { email: "user.contact.email", phone: "user.contact.phone" },
    _id: "user._id"
  };

  private appraisal: Partial<IAppraisal> = {
    id: "appraisal.id",
    status: "appraisal.status" as any,
    total: "appraisal.total" as any,
    currency: "appraisal.currency" as any,
    subTotal: "appraisal.subTotal" as any,
    title: "appraisal.title",
    taxTotal: "appraisal.taxTotal" as any,
    date: "appraisal.date",
    seller: this.getInvoicePartyParts("appraisal.seller") as IInvoiceParty,
    totalLoss: "appraisal.totalLoss" as any,
    residualValueNet: "appraisal.residualValueNet" as any,
    replacementValueNet: "appraisal.replacementValueNet" as any,
    diminishedValueNet: "appraisal.diminishedValueNet" as any
  };

  private partner: Partial<PartnerEntity> = {
    id: "partner.id",
    companyName: "partner.companyName",
    address: {
      street: "partner.address.street",
      zip: "partner.address.zip",
      city: "partner.address.city",
      state: "partner.address.state",
      countryCode: "user.address.countryCode",
      geo: { lat: "partner.address.geo.lat" as any, lng: "partner.address.geo.lng" as any }
    },
    contact: { email: "partner.contact.email", phone: "partner.contact.phone" },
    settings: { defaultUrl: "partner.settings.defaultUrl" } as any,
    companyUsername: "partner.companyUsername"
  };

  private report: Partial<IReport> = {
    id: "report.id",
    partnerId: "report.partnerId",
    externalId: "report.externalId",
    customerName: "report.customerName",
    firstName: "report.firstName",
    lastName: "report.lastName",
    numberplate: "report.numberplate",
    customerAddress: {
      street: "report.customerAddress.street",
      zip: "report.customerAddress.zip",
      city: "report.customerAddress.city",
      state: "report.customerAddress.state"
    },
    customerContact: new Contact({ email: "report.customerContact.email", phone: "report.customerContact.phone" }),
    message: "report.message",
    datePreference: "report.datePreference",
    registrationResults: this.getRegistrationPaths("report"),
    progressStatus: "report.progressStatus" as any,
    broker: { name: "report.broker.name", number: "report.broker.number" },
    insurances: [
      {
        name: "report.insurances.0.name",
        number: "report.insurances.0.number",
        type: "report.insurances.0.type" as any
      }
    ],
    claimNumber: "report.claimNumber",
    title: "report.title",
    status: "report.status",
    company: "report.company",
    isCompany: "report.isCompany" as any,
    companyName: "report.companyName",
    isTaxDeductible: "report.isTaxDeductible" as any,
    taxnumber: "report.taxnumber",
    language: "report.language" as any,
    countryCode: "report.countryCode" as any,
    marketingOptIn: "report.marketingOptIn" as any,
    reportType: "report.reportType" as any,
    damageType: "report.damageType" as any,
    coverageType: "report.coverageType" as any,
    coverage: "report.coverage" as any,
    summary: "report.summary",
    isFixed: "report.isFixed" as any,
    isDrivable: "report.isDrivable" as any,
    isRoadworthy: "report.isRoadworthy" as any,
    isUnderInfluence: "report.isUnderInfluence" as any,
    isValidDriversLicense: "report.isValidDriversLicense" as any,
    whatsAppContactOptIn: "report.whatsAppContactOptIn" as any,
    liabilitySharePercent: "report.liabilitySharePercent" as any,
    isDeductions: "report.isDeductions" as any,
    isDepreciationCredit: "report.isDepreciationCredit" as any,
    totalLoss: "report.totalLoss" as any,
    criticality: "report.criticality" as any,
    isWrapped: "report.isWrapped" as any,
    isWrapDamaged: "report.isWrapDamaged" as any,
    damageLocationDescription: "report.damageLocationDescription",
    repair: {
      status: "report.repair.status" as any,
      isRentalProvided: "report.repair.isRentalProvided" as any,
      notes: "report.repair.notes",
      startDate: "report.repair.startDate",
      endDate: "report.repair.endDate"
    } as any,
    accidentDetails: {
      role: "report.accidentDetails.role" as any,
      date: "report.accidentDetails.date",
      comment: "report.accidentDetails.comment",
      address: this.getAddressWithGeoPaths("report.accidentDetails.address") as any
    } as any,
    policeDetails: {
      isRecorded: "report.policeDetails.isRecorded" as any,
      department: "report.policeDetails.department",
      fileReference: "report.policeDetails.fileReference"
    }
  };

  private thg: Partial<IThg> = {
    payoutConfiguration: {
      revenue: "thg.payoutConfiguration.revenue" as any,
      isFixed: "thg.payoutConfiguration.isFixed" as any
    },
    status: "thg.status" as any,
    bonus: "thg.bonus" as any,
    impactType: "thg.impactType" as any,
    impactFactor: "thg.impactFactor" as any,
    registration: this.getRegistrationPaths("thg"),
    campaign: "thg.campaign",
    code: "thg.code",
    year: "thg.year" as any,
    isMultiyear: "thg.isMultiyear" as any,
    numberplate: "thg.numberplate",
    kownFrom: "thg.knownFrom" as any,
    timestamp: this.getTimestampPaths("thg")
  };

  private billing: Partial<ThgBillingViewmodelGen> = {
    billingNumber: "billing.billingNumber" as any,
    type: "billing.type" as any,
    isPublished: "billing.isPublished" as any,
    userId: "billing.userId" as any,
    accountNumber: "billing.accountNumber" as any,
    timestamp: this.getTimestampPaths("billing")
  };

  private form: Partial<MrfiktivFormDocumentGen> = {
    partnerId: "form.partnerId" as any,
    name: "form.name",
    contact: { email: "form.contact.email", phone: "form.contact.phone" },
    title: "form.title",
    message: "form.message",
    registrationResults: this.getRegistrationResultsPaths("form"),
    timestamps: this.getTimestampsPaths("form")
  };

  private request: Partial<ISignRequest> = {
    title: "request.title",
    description: "request.description",
    recipient: this.getRecipientPaths("request"),
    validBy: "request.validBy",
    status: "request.status" as any,
    documents: "request.documents" as any,
    signatures: "request.signatures" as any,
    timestamp: this.getTimestampPaths("request") as any,
    id: "request.id",
    partnerId: "request.partnerId" as any
  };

  private rental: Partial<IRental> = {
    id: "rental.id",
    status: "rental.status" as any,
    currency: "rental.currency" as any,
    title: "rental.title",
    numberplate: "rental.numberplate",
    isPickup: "rental.isPickup" as any,
    pickupDate: "rental.pickupDate",
    pickupLocation: this.getAddressWithGeoPaths("rental.pickupLocation") as any,
    isDelivery: "rental.isDelivery" as any,
    deliveryDate: "rental.deliveryDate",
    deliveryLocation: this.getAddressWithGeoPaths("rental.deliveryLocation") as any,
    dailyRate: "rental.dailyRate" as any,
    rate: "rental.rate",
    vehicleGroup: "rental.vehicleGroup",
    vehicleModel: "rental.vehicleModel",
    notes: "rental.notes",
    returnDate: "rental.returnDate",
    costCoveredByCustomer: "rental.costCoveredByCustomer" as any,
    costCoveredByInsurance: "rental.costCoveredByInsurance" as any,
    seller: this.getInvoicePartyParts("rental.seller") as IInvoiceParty,
    timestamp: this.getTimestampPaths("rental") as any
  };

  private repairEstimate: Partial<IRepairEstimate> = {
    id: "repairEstimate.id",
    status: "repairEstimate.status" as any,
    total: "repairEstimate.total" as any,
    currency: "repairEstimate.currency" as any,
    subTotal: "repairEstimate.subTotal" as any,
    taxTotal: "repairEstimate.taxTotal" as any,
    number: "repairEstimate.number",
    date: "repairEstimate.date",
    seller: this.getInvoicePartyParts("repairEstimate.seller") as IInvoiceParty,
    timestamp: this.getTimestampPaths("repairEstimate") as any
  };

  private clearanceApproval: Partial<IClearanceApproval> = {
    id: "clearanceApproval.id",
    status: "clearanceApproval.status" as any,
    currency: "clearanceApproval.currency" as any,
    title: "clearanceApproval.title",
    description: "clearanceApproval.description",
    type: "clearanceApproval.type" as any,
    total: "clearanceApproval.total" as any,
    subTotal: "clearanceApproval.subTotal" as any,
    taxTotal: "clearanceApproval.taxTotal" as any,
    due: "clearanceApproval.due",
    seller: this.getInvoicePartyParts("clearanceApproval.seller") as IInvoiceParty,
    timestamp: this.getTimestampPaths("clearanceApproval") as any
  };

  private clearance: Partial<IClearance> = {
    id: "clearance.id",
    status: "clearance.status" as any,
    currency: "clearance.currency" as any,
    title: "clearance.title",
    description: "clearance.description",
    repairStart: "clearance.repairStart",
    repairEnd: "clearance.repairEnd",
    total: "clearance.total" as any,
    subTotal: "clearance.subTotal" as any,
    taxTotal: "clearance.taxTotal" as any,
    seller: this.getInvoicePartyParts("clearance.seller") as IInvoiceParty,
    timestamp: this.getTimestampPaths("clearance") as any
  };

  sign: Partial<SignDigitalSignatureViewModelGen> & { created: string } = {
    firstName: "sign.firstName",
    lastName: "sign.lastName",
    location: "sign.location",
    created: "sign.created"
  };

  get formatters() {
    return Object.values(TemplateFormatterEnum);
  }

  private getTimestampPaths(prefix: string) {
    return {
      created: (prefix + ".timestamp.created") as any,
      lastModified: (prefix + ".timestamp.lastModified") as any,
      modified: (prefix + ".timestamp.modified") as any
    };
  }

  private getTimestampsPaths(prefix: string) {
    return {
      created: (prefix + ".timestamps.created") as any,
      lastModified: (prefix + ".timestamps.lastModified") as any,
      modified: (prefix + ".timestamps.modified") as any
    };
  }

  private getRecipientPaths(prefix: string) {
    return {
      firstName: (prefix + ".recipient.firstName") as any,
      lastName: (prefix + ".recipient.lastName") as any,
      companyName: (prefix + ".recipient.companyName") as any,
      email: (prefix + ".recipient.email") as any
    };
  }

  private getRefPaths(prefix: string) {
    return {
      refType: (prefix + ".ref.type") as any,
      refId: (prefix + ".ref.id") as any
    };
  }

  private getRegistrationPaths(prefix: string) {
    return {
      city: (prefix + ".registration.city") as any,
      firstname: (prefix + ".registration.firstname") as any,
      firstregistrationDay: prefix + ".registration.firstregistrationDay",
      firstregistrationMonth: prefix + ".registration.firstregistrationMonth",
      firstregistrationYear: prefix + ".registration.firstregistrationYear",
      huMonth: prefix + ".registration.huMonth",
      huYear: prefix + ".registration.huYear",
      identificationnumber: prefix + ".registration.identificationnumber",
      manufacturerName: prefix + ".registration.manufacturerName",
      manufacturerType: prefix + ".registration.manufacturerType",
      manufacturerDescription: prefix + ".registration.manufacturerDescription",
      manufacturerNameCode: prefix + ".registration.manufacturerNameCode",
      manufacturerTypeCode: prefix + ".registration.manufacturerTypeCode",
      name: prefix + ".registration.name",
      numberplate: prefix + ".registration.numberplate",
      zipCode: prefix + ".registration.zipCode",
      street: prefix + ".registration.street",
      driveTyp: prefix + ".registration.driveTyp",
      vehicleClass: prefix + ".registration.vehicleClass"
    } as any;
  }

  private getAddressWithGeoPaths(prefix: string) {
    return {
      street: `${prefix}.street`,
      zip: `${prefix}.zip`,
      city: `${prefix}.city`,
      state: `${prefix}.state`,
      countryCode: `${prefix}.countryCode`,
      geo: {
        lat: `${prefix}.geo.lat` as any,
        lng: `${prefix}.geo.lng` as any
      }
    };
  }

  private getInvoicePartyParts(prefix: string): Partial<IInvoiceParty> {
    return {
      name: prefix + ".name",
      email: prefix + ".email",
      phone: prefix + ".phone",
      taxnumber: prefix + ".taxnumber"
    };
  }

  private getRegistrationResultsPaths(prefix: string) {
    return {
      city: (prefix + ".registrationResults.city") as any,
      firstname: (prefix + ".registrationResults.firstname") as any,
      firstregistrationDay: prefix + ".registrationResults.firstregistrationDay",
      firstregistrationMonth: prefix + ".registrationResults.firstregistrationMonth",
      firstregistrationYear: prefix + ".registrationResults.firstregistrationYear",
      huMonth: prefix + ".registrationResults.huMonth",
      huYear: prefix + ".registrationResults.huYear",
      identificationnumber: prefix + ".registrationResults.identificationnumber",
      manufacturerNameCode: prefix + ".registrationResults.manufacturerNameCode",
      manufacturerTypeCode: prefix + ".registrationResults.manufacturerTypeCode",
      name: prefix + ".registrationResults.name",
      numberplate: prefix + ".registrationResults.numberplate",
      zipCode: prefix + ".registrationResults.zipCode",
      street: prefix + ".registrationResults.street",
      driveTyp: prefix + ".registrationResults.driveTyp",
      vehicleClass: prefix + ".registrationResults.vehicleClass"
    } as any;
  }

  private getNestedKeys(object: Record<any, any>) {
    const keys: string[] = [];
    for (const val of Object.values(object)) {
      if (val instanceof Object) {
        keys.push(...this.getNestedKeys(val));
      } else {
        keys.push(val);
      }
    }

    return keys;
  }

  private get keysAndTranslations() {
    const context = this.getContext();

    this.$log.debug(context);

    const nestedKeys = this.getNestedKeys(context);

    const keysAndTranslations = nestedKeys.map((key) => {
      return { key, translation: this.$t(`components.template.editor.placeholder.path.${key}`).toString() };
    });

    return keysAndTranslations;
  }

  get options() {
    return this.keysAndTranslations.map((k) => k.translation);
  }

  get optionDisplay(): string {
    if (this.hasEditedOption) {
      return this.option;
    }

    return this.initialOption ? displayLabelForToken(this.initialOption, (key) => this.$t(key).toString()) : "";
  }

  set optionDisplay(value: string) {
    this.hasEditedOption = true;
    this.option = value;
  }

  private getKeyForTranslation(translation: string) {
    const key = this.keysAndTranslations.find((k) => k.translation === translation)?.key || translation;

    return key;
  }

  onChangeSelection() {
    this.$emit("change", this.select());
  }

  focus() {
    const combobox = this.$refs.combobox as { focus?: () => void } | undefined;
    combobox?.focus?.();
  }

  open() {
    const combobox = this.$refs.combobox as
      | { focus?: () => void; isMenuActive?: boolean; activateMenu?: () => void }
      | undefined;
    combobox?.focus?.();
    if (combobox?.activateMenu) {
      combobox.activateMenu();
      return;
    }
    if (combobox) {
      combobox.isMenuActive = true;
    }
  }

  get me(): IUser {
    return UserModule.user;
  }

  private getContext() {
    const context: any = {};

    if (this.context) {
      if (this.context.report) {
        context.report = this.report;
      }
      if (this.context.partner) {
        context.partner = this.partner;
      }
      if (this.context.thg) {
        context.thg = this.thg;
      }
      if (this.context.user) {
        context.user = this.user;
      }
      if (this.context.billing) {
        context.billing = this.billing;
      }
      if (this.context.form) {
        context.form = this.form;
      }
      if (this.context.request) {
        context.request = this.request;
      }
      if (this.context.url) {
        context.url = this.url;
      }
      if (this.context.sign) {
        context.sign = this.sign;
      }

      context.my = this.me;

      return context;
    }

    // Admin Context
    if (!this.partnerId) {
      context.report = this.report;
      context.partner = this.partner;
      context.thg = this.thg;
      context.user = this.user;
      context.billing = this.billing;
      context.form = this.form;
      context.request = this.request;
      context.url = this.url;
      context.sign = this.sign;
      context.my = this.my;
      context.appraisal = this.appraisal;
      context.rental = this.rental;
      context.clearance = this.clearance;
      context.clearanceApproval = this.clearanceApproval;
      context.repairEstimate = this.repairEstimate;

      return context;
    }

    // REPORT_PORTAL Context
    if (ConfigModule.appContext === AppContextEnum.REPORT_PORTAL) {
      context.partner = this.partner;
      context.report = this.report;
      context.form = this.form;
      context.request = this.request;
      context.sign = this.sign;
      context.url = this.url;
      context.my = this.my;
      context.appraisal = this.appraisal;
      context.rental = this.rental;
      context.clearance = this.clearance;
      context.clearanceApproval = this.clearanceApproval;
      context.repairEstimate = this.repairEstimate;

      return context;
    }

    // THG_PORTAL Context
    if (ConfigModule.appContext === AppContextEnum.THG_PORTAL) {
      context.partner = this.partner;
      context.thg = this.thg;
      context.user = this.user;
      context.billing = this.billing;
      context.request = this.request;
      context.sign = this.sign;
      context.my = this.my;

      return context;
    }
  }

  /**
   * Gets the token category from the option
   *
   * @param option
   */
  private getTokenCategory(option: string): TokenCategoriesEnum {
    return option.split(".")[0] as TokenCategoriesEnum;
  }

  /**
   * Verifies that token categories like setting/custom that are not in the user categories are not nested
   *
   * @param option
   */
  private verifyNesting(option: string): void {
    const tokenCategory = this.getTokenCategory(option);

    if (!TemplateTokenConfig.userCategories.includes(tokenCategory)) {
      return;
    }

    if (this.option.split(".").length > 2) {
      const errorString = "User settings should not be nested!";

      this.$toast.error(errorString);
      throw new Error(errorString);
    }
  }

  /**
   * Verifies that the token category is known
   *
   * @param option
   */
  private verifyCategory(option: string) {
    const tokenCategory = this.getTokenCategory(option);

    if (!TemplateTokenConfig.predefinedCategories.includes(tokenCategory)) {
      const errorText =
        "A setting must have one of the following prefixes: " + TemplateTokenConfig.predefinedCategories.join(", ");

      this.$toast.error(errorText);
      throw new Error(errorText);
    }
  }

  private formatPlaceholder(placeholder: string, formatter: string) {
    if (formatter === TemplateFormatterEnum.ESCAPE) {
      return `{{{${placeholder}}}}`;
    }

    placeholder = `{{${placeholder}}}`;
    let placeholderFormatterPrefix = "";
    let placeholderFormatterSuffix = "";
    if (this.isFormatter && formatter) {
      placeholderFormatterPrefix = `{{#${formatter}}} `;
      placeholderFormatterSuffix = ` {{/${formatter}}}`;
    }

    return `${placeholderFormatterPrefix}${placeholder}${placeholderFormatterSuffix}`;
  }

  /**
   * Verify the token category and return it formatted
   */
  select() {
    const token = this.getKeyForTranslation(this.option);

    this.verifyNesting(token);
    this.verifyCategory(token);

    return this.formatPlaceholder(token, this.formatter);
  }
}
</script>

<style scoped>
/* Fix margin below placeholder dropdown and align format button */
.template-placeholder-row {
  margin-bottom: 0 !important;
}
.template-placeholder-dropdown-col > .v-input,
.template-placeholder-dropdown-col > .v-input--is-focused,
.template-placeholder-dropdown-col > .v-combobox {
  margin-bottom: 4px !important;
}
.template-placeholder-dropdown-col {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
.template-placeholder-format-col {
  display: flex;
  align-items: center !important;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  height: 100%;
}
.template-placeholder-format-col .v-input--selection-controls {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  align-items: center !important;
}
.scrollable {
  overflow-y: auto;
}
</style>
